import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "tm_token";
export const USER_KEY = "tm_user";

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const CATEGORIES = [
  { slug: "top", label: "Top" },
  { slug: "world", label: "World" },
  { slug: "technology", label: "Technology" },
  { slug: "business", label: "Business" },
  { slug: "sports", label: "Sports" },
  { slug: "entertainment", label: "Entertainment" },
  { slug: "science", label: "Science" },
  { slug: "health", label: "Health" },
];

// News
export const fetchTrending = (category = "top", country = "US", page = 1, page_size = 12) =>
  api.get(`/news/trending`, { params: { category, country, page, page_size } }).then((r) => r.data);

export const fetchTrendingMeta = (category, country) =>
  api.get(`/news/trending/meta`, { params: { category, country } }).then((r) => r.data);

export const fetchNewsById = (id) =>
  api.get(`/news/${id}`).then((r) => r.data);

export const fetchArticleBySlug = (slug) =>
  api.get(`/articles/${slug}`).then((r) => r.data);

export const enrichNews = (id) =>
  api.get(`/news/${id}/enrich`).then((r) => r.data);

export const searchNews = (q, country = "US", page = 1, page_size = 12) =>
  api.get(`/news/search`, { params: { q, country, page, page_size } }).then((r) => r.data);

// AI
export const generateYouTubeKit = async (payload) => {
  const { data } = await api.post(`/generate/youtube`, payload);
  // If cached, the response contains the full content object directly
  if (data.script !== undefined || data.image_prompts !== undefined) {
    return data;
  }
  // Otherwise it's a background job — poll until complete
  const jobId = data.job_id;
  const MAX_POLLS = 90; // ~3 minutes at 2s intervals
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data: job } = await api.get(`/generate/youtube/status/${jobId}`);
    if (job.status === "complete") return job.data;
    if (job.status === "error") {
      const err = new Error(job.error || "Content generation failed. Please try again.");
      throw err;
    }
  }
  throw new Error("Generation timed out. Please try again.");
};

export const generateImage = (prompt) =>
  api.post(`/generate/image`, { prompt }).then((r) => r.data);

export const generateSeoContent = (payload) =>
  api.post(`/generate/seo`, payload).then((r) => r.data);

export const fetchHistory = (limit = 20) =>
  api.get(`/history`, { params: { limit } }).then((r) => r.data);

// Auth
export const register = (payload) =>
  api.post(`/auth/register`, payload).then((r) => r.data);

export const login = (payload) =>
  api.post(`/auth/login`, payload).then((r) => r.data);

export const googleSession = (session_id, country, timezone) =>
  api.post(`/auth/google-session`, { session_id, country, timezone }).then((r) => r.data);

export const fetchMe = () =>
  api.get(`/auth/me`).then((r) => r.data);

export const updateMe = (payload) =>
  api.patch(`/auth/me`, payload).then((r) => r.data);

// Settings
export const fetchPublicSettings = () =>
  api.get(`/settings/public`).then((r) => r.data);

export const adminModels = () => api.get(`/admin/models`).then((r) => r.data);
export const adminUploadLogo = (image) => api.post(`/admin/logo`, { image }).then((r) => r.data);
export const adminDeleteLogo = () => api.delete(`/admin/logo`).then((r) => r.data);

// Custom models
export const adminListCustomModels = () => api.get(`/admin/custom-models`).then((r) => r.data);
export const adminAddCustomModel = (payload) => api.post(`/admin/custom-models`, payload).then((r) => r.data);
export const adminUpdateCustomModel = (id, payload) => api.patch(`/admin/custom-models/${id}`, payload).then((r) => r.data);
export const adminDeleteCustomModel = (id, kind) => api.delete(`/admin/custom-models/${id}`, { params: { kind } }).then((r) => r.data);

// Providers
export const adminListProviders = () => api.get(`/admin/providers`).then((r) => r.data);
export const adminAddProvider = (payload) => api.post(`/admin/custom-providers`, payload).then((r) => r.data);
export const adminDeleteProvider = (name) => api.delete(`/admin/custom-providers/${encodeURIComponent(name)}`).then((r) => r.data);

// Plans
export const fetchPlans = () => api.get(`/plans`).then((r) => r.data);
export const adminPlans = () => api.get(`/admin/plans`).then((r) => r.data);
export const adminUpdatePlan = (id, payload) => api.patch(`/admin/plans/${id}`, payload).then((r) => r.data);

// Payments
export const fetchPaymentsPublicSettings = () => api.get(`/payments/settings`).then((r) => r.data);
export const adminPaymentSettings = () => api.get(`/admin/payment-settings`).then((r) => r.data);
export const adminUpdatePaymentSettings = (payload) => api.patch(`/admin/payment-settings`, payload).then((r) => r.data);
export const createCheckout = (plan_id, origin_url) => api.post(`/payments/checkout`, { plan_id, origin_url }).then((r) => r.data);
export const fetchPaymentStatus = (session_id) => api.get(`/payments/status/${session_id}`).then((r) => r.data);
export const fetchMySubscription = () => api.get(`/payments/my-subscription`).then((r) => r.data);
export const cancelSubscription = () => api.post(`/payments/cancel-subscription`).then((r) => r.data);
export const adminPaymentAnalytics = () => api.get(`/admin/payment-analytics`).then((r) => r.data);

// Admin
export const adminSettings = {
  get: () => api.get(`/admin/settings`).then((r) => r.data),
  update: (payload) => api.patch(`/admin/settings`, payload).then((r) => r.data),
};

export const adminUsers = () => api.get(`/admin/users`).then((r) => r.data);
export const adminDeleteUser = (user_id) => api.delete(`/admin/users/${user_id}`).then((r) => r.data);
export const adminUpdateUserPremium = (user_id, is_premium) => api.patch(`/admin/users/${user_id}/premium`, { is_premium }).then((r) => r.data);
export const adminAnalytics = () => api.get(`/admin/analytics`).then((r) => r.data);
export const adminConfig = () => api.get(`/admin/config`).then((r) => r.data);

// Auth — Email flows
export const forgotPassword = (email) =>
  api.post(`/auth/forgot-password`, { email }).then((r) => r.data);

export const resetPassword = (token, new_password) =>
  api.post(`/auth/reset-password`, { token, new_password }).then((r) => r.data);

export const verifyEmail = (token) =>
  api.get(`/auth/verify-email`, { params: { token } }).then((r) => r.data);

export const resendVerification = (email) =>
  api.post(`/auth/resend-verification`, { email }).then((r) => r.data);

// Contact
export const submitContactForm = (payload) =>
  api.post(`/contact`, payload).then((r) => r.data);

// Social links
export const fetchSocialLinks = () =>
  api.get(`/social-links`).then((r) => r.data);

export const adminGetSocialLinks = () =>
  api.get(`/admin/social-links`).then((r) => r.data);

export const adminUpdateSocialLinks = (platforms) =>
  api.patch(`/admin/social-links`, { platforms }).then((r) => r.data);

// Newsletter
export const newsletterSubscribe = (email, source = "web") =>
  api.post(`/newsletter/subscribe`, { email, source }).then((r) => r.data);

export const newsletterUnsubscribe = (email) =>
  api.post(`/newsletter/unsubscribe`, { email }).then((r) => r.data);

export const newsletterConfirm = (token) =>
  api.get(`/newsletter/confirm`, { params: { token } }).then((r) => r.data);

export const newsletterUnsubscribeByToken = (token) =>
  api.get(`/newsletter/unsubscribe`, { params: { token } }).then((r) => r.data);

// Admin — Email config
export const adminGetEmailSettings = () =>
  api.get(`/admin/email-settings`).then((r) => r.data);

export const adminUpdateEmailSettings = (payload) =>
  api.patch(`/admin/email-settings`, payload).then((r) => r.data);

export const adminTestEmail = (to) =>
  api.post(`/admin/email-settings/test`, { to }).then((r) => r.data);

export const adminNewsletterSubscribers = (page = 1, page_size = 50) =>
  api.get(`/admin/newsletter-subscribers`, { params: { page, page_size } }).then((r) => r.data);

// Geo Insights
export const trackVisit = (page = "/") => {
  const referrer = document.referrer || null;
  const screen_width = window.screen?.width || null;
  const screen_height = window.screen?.height || null;
  return api.post(`/track-visit`, { page, referrer, screen_width, screen_height }).catch(() => {});
};

export const adminGeoInsights = (days = 30, months = 12) =>
  api.get(`/admin/geo-insights`, { params: { days, months } }).then((r) => r.data);

export const adminGeoRealtime = () =>
  api.get(`/admin/geo-insights/realtime`).then((r) => r.data);

export const adminGeoDevices = (days = 30) =>
  api.get(`/admin/geo-insights/devices`, { params: { days } }).then((r) => r.data);

export const adminGeoPages = (days = 30, page = 1, pageSize = 10) =>
  api.get(`/admin/geo-insights/pages`, { params: { days, page, page_size: pageSize } }).then((r) => r.data);

export const adminGeoSummary = () =>
  api.get(`/admin/geo-insights/summary`).then((r) => r.data);

export const adminGeoSources = (days = 30) =>
  api.get(`/admin/geo-insights/sources`, { params: { days } }).then((r) => r.data);

export const adminGeoHourly = (days = 7) =>
  api.get(`/admin/geo-insights/hourly`, { params: { days } }).then((r) => r.data);

export const adminGeoLiveFeed = (limit = 20) =>
  api.get(`/admin/geo-insights/live-feed`, { params: { limit } }).then((r) => r.data);

// SEO Studio admin
export const adminSeoStudio = () => api.get(`/admin/seo-studio`).then((r) => r.data);
export const adminUpdateSeoSettings = (payload) => adminSettings.update(payload);

export const adminGeoExport = (days = 30) =>
  api.get(`/admin/geo-insights/export`, { params: { days }, responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `geo-analytics-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  });

// AI Platform
export const fetchAISection = (section, params = {}) =>
  api.get(`/ai/${section}`, { params }).then((r) => {
    const data = r.data;
    // Return paginated object so useInfiniteFeed can use has_more; fall back for legacy bare arrays
    if (Array.isArray(data)) return { items: data, has_more: data.length >= (params.page_size ?? 12) };
    return data;
  });

export const fetchAISectionPaginated = (section, params = {}) =>
  api.get(`/ai/${section}`, { params }).then((r) => {
    const data = r.data;
    if (Array.isArray(data)) return { items: data, total: data.length, page: 1, has_more: false };
    return data;
  });

export const fetchAIItem = (section, itemId) =>
  api.get(`/ai/${section}/${itemId}`).then((r) => r.data);

// Admin — AI Data Jobs
export const adminListAIJobs = () =>
  api.get(`/admin/ai-jobs`).then((r) => r.data);

export const adminUpdateAIJob = (section, payload) =>
  api.patch(`/admin/ai-jobs/${section}`, payload).then((r) => r.data);

export const adminTriggerAIJob = (section) =>
  api.post(`/admin/ai-jobs/${section}/run`).then((r) => r.data);

export const adminBulkSeedAIJob = (section, runs = 20, delay_seconds = 10) =>
  api.post(`/admin/ai-jobs/${section}/bulk-seed`, { runs, delay_seconds }).then((r) => r.data);

export const adminAIJobLogs = (section, limit = 20) =>
  api.get(`/admin/ai-jobs/${section}/logs`, { params: { limit } }).then((r) => r.data);

export const adminGetAIDataModel = () =>
  api.get(`/admin/ai-data-model`).then((r) => r.data);

// Affiliate redirect info
export const fetchGoRedirect = (slug) =>
  api.get(`/go/${slug}`).then((r) => r.data);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  return String(detail);
}
